import {
  initializeAppCheck,
  onTokenChanged,
  ReCaptchaV3Provider,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';

function isRecaptchaEnterpriseEnabled() {
  const raw = process.env.REACT_APP_FIREBASE_APPCHECK_USE_RECAPTCHA_ENTERPRISE;
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return v === '1' || v === 'true';
}

/**
 * Enables Firebase App Check when a public site key is set.
 * The JS provider must match Console → App Check → Web:
 * - reCAPTCHA v3 → ReCaptchaV3Provider (default);
 * - reCAPTCHA Enterprise → REACT_APP_FIREBASE_APPCHECK_USE_RECAPTCHA_ENTERPRISE=1.
 * A v3 provider with an Enterprise site key returns 400 on exchangeRecaptchaV3Token
 * and then throttles; Firestore Listen/Write can 400 even while enforcement is off.
 *
 * @param {import('firebase/app').FirebaseApp} app
 * @returns {import('firebase/app-check').AppCheck | null}
 * @see https://firebase.google.com/docs/app-check/web/recaptcha-provider
 * @see https://firebase.google.com/docs/app-check/web/recaptcha-enterprise-provider
 */
export function initFirebaseAppCheck(app) {
  const siteKey = process.env.REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_SITE_KEY?.trim() ?? '';
  if (!siteKey) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.info(
        '[Firebase App Check] Disabled: set REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_SITE_KEY ' +
          '(Firebase Console → App Check → Web → reCAPTCHA v3 или Enterprise site key).'
      );
    }
    return null;
  }

  const debugRaw = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN?.trim() ?? '';
  // Localhost + ad blockers break reCAPTCHA and flood Auth/Firestore with
  // appCheck/recaptcha-error. Require an explicit debug token in development.
  if (process.env.NODE_ENV === 'development' && !debugRaw) {
    // eslint-disable-next-line no-console
    console.info(
      '[Firebase App Check] Skipped in development (no REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN). ' +
        'Site key alone hangs chat bootstrap when reCAPTCHA fails.'
    );
    return null;
  }

  if (process.env.NODE_ENV === 'development' && debugRaw && typeof window !== 'undefined') {
    if (debugRaw === 'true' || debugRaw === '1') {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    } else {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = debugRaw;
    }
    // eslint-disable-next-line no-console
    console.info(
      '[Firebase App Check] Debug mode: register the token in Console → App Check → Manage debug tokens ' +
        '(if REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN=true, copy the token from the browser console).'
    );
  }

  const enterprise = isRecaptchaEnterpriseEnabled();
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.info(
      `[Firebase App Check] Provider: ${enterprise ? 'reCAPTCHA Enterprise' : 'reCAPTCHA v3'}`
    );
  }

  try {
    const appCheck = initializeAppCheck(app, {
      provider: enterprise
        ? new ReCaptchaEnterpriseProvider(siteKey)
        : new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    onTokenChanged(
      appCheck,
      () => {},
      (err) => {
        // 400 / throttle must not become an unhandled rejection during Auth token attach.
        console.warn('[Firebase App Check] token error', err?.code || err);
      }
    );
    return appCheck;
  } catch (err) {
    console.warn('[Firebase App Check] init failed; continuing without App Check', err);
    return null;
  }
}
